/**
 * Staff actions on a customer account (QA 2026-09-28 #7): edit its account and billing data
 * (login e-mail / phone, language, status, tier, currency, invoice details — identity data are
 * the primary subject's, edited with the subject form) and add a sign-in user. Both need `customer.manage`, a reason (audited) and an Idempotency-Key. A new user's
 * first password is shown once here and never again (the server keeps only its hash).
 */

import { Pencil, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '../../api';
import type { CustomerAccount, CustomerEdit, CustomerStatus, CustomerUserAdded, NewCustomerUser, UserRelation } from '../../api/areas/accounts';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { accountFromCustomer, accountPhone, checkAccount, invoicePreview, toProfileInput, type AccountDraft, type FieldProblem } from '../../lib/customerProfile';
import { addressLine } from '../../lib/address';
import { useApi } from '../../lib/useApi';
import { AccountFields, InvoiceFields } from './CustomerProfileFields';
import { useTierLabel } from './shared';
import { ActionDialog, FormFooter, ReasonField, SecretOnce, selectClass, type DialogBodyProps } from '../operator/shared';

const STATUSES: CustomerStatus[] = ['ACTIVE', 'SUSPENDED', 'CLOSED'];
const RELATIONS: UserRelation[] = ['OWNER', 'ADMIN', 'BILLING', 'TECHNICAL', 'READ_ONLY'];

type Lookup = (key: string, o?: Record<string, unknown>) => string;

/** Opened from the customer page's "Thao tác" menu instead of its own button. */
type DialogControl = { open?: boolean; onOpenChange?: (open: boolean) => void; trigger?: boolean };

/** The primary subject as the edit form needs it: its type, address country, and what
 * "giống chủ thể chính" puts on an invoice. */
function usePrimary(subjectId: string | null | undefined) {
  const profile = useApi(
    (signal) => (subjectId ? api.getSubjectProfile(subjectId, { signal }) : Promise.resolve(null)),
    [subjectId],
  );
  const p = profile.status === 'success' ? profile.data : null;
  const personalId = p?.identifiers.find((i) => i.type === 'NATIONAL_ID' || i.type === 'PASSPORT');
  const orgId = p?.identifiers.find((i) => i.type === 'TAX_CODE' || i.type === 'ENTERPRISE_CODE');
  const person = p ? p.subject_type === 'INDIVIDUAL' || p.subject_type === 'HOUSEHOLD_BUSINESS' : false;
  const address = p?.vn_address?.full_address ?? (p?.address ? addressLine(p.address) : null);
  return {
    subjectType: p?.subject_type ?? 'INDIVIDUAL',
    country: (p?.address?.country_code ?? p?.country_code ?? 'VN').toUpperCase(),
    name: p?.legal_name ?? '',
    taxCode: person ? (personalId?.hint ?? null) : (orgId?.hint ?? null),
    address,
    person,
  };
}

function EditForm({ account, close, changed }: DialogBodyProps & { account: CustomerAccount }) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as Lookup;
  const tiersApi = useApi((signal) => api.listCustomerTiers({ signal }), []);
  const primary = usePrimary(account.primary_subject_id);
  const [d, setD] = useState<AccountDraft>(() => accountFromCustomer(account));
  const [status, setStatus] = useState<CustomerStatus>(account.status);
  const [tier, setTier] = useState(account.tier);
  const [currency, setCurrency] = useState(account.billing_currency);
  const [reason, setReason] = useState('');
  const [local, setLocal] = useState<FieldProblem[]>([]);
  const set = (patch: Partial<AccountDraft>) => setD((x) => ({ ...x, ...patch }));
  const w = useWrite((b: CustomerEdit, opts) => api.updateCustomer(account.id, b, opts));
  const done = w.state.phase === 'done' ? w.state.result : null;
  const server = w.state.phase === 'error' ? fieldProblems(w.state.error) : [];
  const problems = [...local, ...server];
  const tierLabel = useTierLabel();
  const known: [string, string][] = tiersApi.status === 'success' ? tiersApi.data.items.map((x) => [x.code, tierLabel(x)]) : [];
  // The account's own tier stays choosable while the catalogue loads.
  const tiers = known.some(([code]) => code === account.tier) ? known : [[account.tier, account.tier] as [string, string], ...known];
  if (done) {
    return (
      <div className="grid gap-3">
        <Callout tone="ok">{done.changed.length ? t('customerActions.saved') : t('customerActions.noChange')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  const preview = invoicePreview({ name: primary.name || account.display_name, subjectType: primary.subjectType, taxCode: primary.taxCode, address: primary.address }, d.email);
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found = checkAccount(d, primary.subjectType, primary.country);
        setLocal(found);
        if (found.length) return;
        void w
          .run({
            email: d.email.trim().toLowerCase(),
            phone: accountPhone(d, primary.country),
            billing_currency: currency,
            tier,
            status,
            profile: toProfileInput(d),
            expected_version: account.version,
            reason: reason.trim(),
          })
          .then((r) => r && changed());
      }}
    >
      <Callout tone="info">{t('customerActions.identityOnSubject')}</Callout>
      <AccountFields d={d} set={set} problems={problems} country={primary.country} excludeId={account.id}>
        <Field label={t('customerActions.status')} hint={t('customerActions.statusHint')}>
          <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value as CustomerStatus)}>
            {STATUSES.map((x) => (
              <option key={x} value={x}>
                {tr(`enum.customerStatus.${x}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('customerActions.tier')}>
          <select className={selectClass} value={tier} onChange={(e) => setTier(e.target.value)}>
            {tiers.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('customerActions.currency')} hint={account.has_ledger_activity ? t('customerActions.currencyLocked') : undefined}>
          <select className={selectClass} value={currency} disabled={account.has_ledger_activity} onChange={(e) => setCurrency(e.target.value)}>
            {[...new Set([account.billing_currency, 'VND', 'USD'])].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </Field>
      </AccountFields>
      <InvoiceFields d={d} set={set} problems={problems} preview={preview} personalTaxCode={primary.person} />
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' && server.length === 0 ? <WriteError error={w.state.error} /> : null}
      {problems.length ? <p className="text-sm text-critical">{t('customerForm.fixFields', { count: problems.length })}</p> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('customerActions.save')} onClose={close} />
    </form>
  );
}

function fieldProblems(error: unknown): FieldProblem[] {
  if (!isApiError(error) || error.code !== 'VALIDATION_FAILED') return [];
  const fields = (error.details as { fields?: FieldProblem[] } | null)?.fields;
  return Array.isArray(fields) ? fields : [];
}

function EditBody({ customerId, close, changed }: DialogBodyProps & { customerId: string }) {
  const { t } = useTranslation('accounts');
  const account = useApi((signal) => api.getCustomerAccount(customerId, { signal }), [customerId]);
  if (account.status === 'loading') return <Loading label={t('customerActions.loading')} rows={3} />;
  if (account.status === 'error') return <WriteError error={account.error} />;
  return <EditForm account={account.data} close={close} changed={changed} />;
}

/** "Sửa thông tin khách hàng". */
export function EditCustomerAction({ customerId, onChanged, ...control }: { customerId: string; onChanged: () => void } & DialogControl) {
  const { t } = useTranslation('accounts');
  return (
    <ActionDialog label={t('customerActions.edit')} icon={Pencil} title={t('customerActions.editTitle')} description={t('customerActions.editDescription')} wide onChanged={onChanged} {...control}>
      {(p) => <EditBody customerId={customerId} {...p} />}
    </ActionDialog>
  );
}

function AddUserBody({ customerId, close, changed }: DialogBodyProps & { customerId: string }) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as Lookup;
  const [d, setD] = useState<Omit<NewCustomerUser, 'reason'>>({ email: '', display_name: '', relation: 'ADMIN' });
  const [reason, setReason] = useState('');
  const w = useWrite((b: NewCustomerUser, opts) => api.addCustomerUser(customerId, b, opts));
  const result: CustomerUserAdded | null = w.state.phase === 'done' ? w.state.result : null;
  if (result) {
    return (
      <div className="grid gap-3">
        <Callout tone="ok">{result.created ? t('customerActions.created') : t('customerActions.linked')}</Callout>
        {result.initial_password ? (
          <>
            <Callout tone="warn">{t('customerActions.initialPasswordHint')}</Callout>
            <SecretOnce label={t('customerActions.initialPassword')} value={result.initial_password} />
          </>
        ) : result.secret_already_shown ? (
          <Callout tone="warn">{t('customerActions.alreadyShown')}</Callout>
        ) : null}
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ ...d, email: d.email.trim().toLowerCase(), display_name: d.display_name.trim(), reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('customerActions.userEmail')}>
        <Input type="email" value={d.email} onChange={(e) => setD((x) => ({ ...x, email: e.target.value }))} required autoComplete="off" />
      </Field>
      <Field label={t('customerActions.userName')}>
        <Input value={d.display_name} onChange={(e) => setD((x) => ({ ...x, display_name: e.target.value }))} required maxLength={255} />
      </Field>
      <Field label={t('customerActions.relation')}>
        <select className={selectClass} value={d.relation} onChange={(e) => setD((x) => ({ ...x, relation: e.target.value as UserRelation }))}>
          {RELATIONS.map((r) => (
            <option key={r} value={r}>
              {tr(`enum.relation.${r}`)}
            </option>
          ))}
        </select>
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('customerActions.create')} onClose={close} />
    </form>
  );
}

/** "Thêm người dùng". */
export function AddCustomerUserAction({ customerId, onChanged, ...control }: { customerId: string; onChanged: () => void } & DialogControl) {
  const { t } = useTranslation('accounts');
  return (
    <ActionDialog label={t('customerActions.addUser')} icon={UserPlus} title={t('customerActions.addUserTitle')} description={t('customerActions.addUserDescription')} onChanged={onChanged} {...control}>
      {(p) => <AddUserBody customerId={customerId} {...p} />}
    </ActionDialog>
  );
}
